import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { ClearOutlined, CopyOutlined } from '@ant-design/icons'
import { Button, Tooltip } from 'antd'
import { includes, isNil, noop, size, toUpper } from 'lodash'
import PropTypes from 'prop-types'

import { useQueryParams } from '../../../core/hooks'

import {
  AiHighlightDecoratedComponent,
  ReadMore,
  RichTextHighlightingInput,
} from '../../../common/components/sections'
import { HOTKEYS_ENABLED_TAGS, PAGINATION } from '../../../common/constants'
import { handleAiHighlightStrategy } from '../../../common/helpers'
import {
  clearMiscAndChoiceFromText,
  containsMiscOrChoiceText,
  stripHtmlFromText,
} from '../../../common/utils'

import { DISH_FIELDS_CONFIG, DISHES_AVAILABLE_HOTKEYS } from '../../constants'
import {
  useDishEditableCellHotkey,
  useDishSuggestCurationAndUpdate,
} from '../../hooks'
import { dishPropType } from '../../prop-types'
import {
  StyledEditableTextActionButtonsContainer,
  StyledEditableTextDivCell,
  StyledEditableTextDivContainer,
} from '../styled'

const DishEditableHighlightingTextFieldCell = ({
  fieldName,
  isDisabled,
  dish,
  isTextEditingEnabled,
  shouldIncreaseMarginOnEdit,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const richTextHighlightingInputRef = useRef()
  const [suggestAndUpdate] = useDishSuggestCurationAndUpdate(dish?.id)
  const { buildQueryParams } = useQueryParams()

  const { isSelectInHotkeyEditMode, setHotkeyEditModeEnabled, isFocusOnCell } =
    useDishEditableCellHotkey(dish.id, fieldName)

  const styledFieldName = useMemo(
    () =>
      `styled${toUpper(fieldName[0])}${fieldName.substring(
        1,
        size(fieldName),
      )}`,
    [fieldName],
  )

  const currentValue = useMemo(
    () => dish[styledFieldName] || dish[fieldName] || '',
    [dish, fieldName, styledFieldName],
  )

  const hasMiscAndChoice = useMemo(
    () => containsMiscOrChoiceText(currentValue),
    [currentValue],
  )

  const hasMarginTop = useMemo(
    () => shouldIncreaseMarginOnEdit && isEditing,
    [isEditing, shouldIncreaseMarginOnEdit],
  )

  const highlights = useMemo(
    () => dish?.highlights?.[fieldName] || [],
    [dish?.highlights, fieldName],
  )

  const isEmptyCurrentValue = useMemo(
    () => stripHtmlFromText(currentValue) === '',
    [currentValue],
  )

  const showActionButtons = useMemo(
    () => hasMiscAndChoice || fieldName === DISH_FIELDS_CONFIG.NAME.value,
    [fieldName, hasMiscAndChoice],
  )

  const handleSave = useCallback(
    newValue => {
      if (!isNil(newValue) && newValue !== currentValue) {
        setTimeout(() => {
          suggestAndUpdate(
            { shouldSuggestOnlyLabelsAndIngredients: true },
            {
              [styledFieldName]: newValue,
              ...(isTextEditingEnabled && {
                [fieldName]: stripHtmlFromText(newValue),
              }),
            },
          )
        }, 0)
      }
    },
    [
      currentValue,
      fieldName,
      isTextEditingEnabled,
      styledFieldName,
      suggestAndUpdate,
    ],
  )

  const toggleEditMode = useCallback(
    newIsEditing => {
      if (isDisabled) {
        return
      }

      setIsEditing(newIsEditing)
      setHotkeyEditModeEnabled(false)
    },
    [isDisabled, setHotkeyEditModeEnabled],
  )

  const handleClearMiscAndChoiceText = useCallback(
    () => handleSave(clearMiscAndChoiceFromText(currentValue)),
    [currentValue, handleSave],
  )

  const handleCopyDishNameAndLink = useCallback(() => {
    const dishPath = buildQueryParams({
      dishIds: [dish.id],
      shouldScrollToDish: true,
      currentPage: PAGINATION.DEFAULT_PAGE,
    })

    const dishLink = `${window.location.origin}${dishPath}`

    navigator.clipboard.writeText(`=HYPERLINK("${dishLink}"; "${dish.name}")`)
  }, [buildQueryParams, dish.id, dish.name])

  useEffect(() => {
    if (isSelectInHotkeyEditMode) {
      setIsEditing(true)
    }
  }, [isSelectInHotkeyEditMode])

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.ESC.hotkey,
    noop,
    {
      enabled: isEditing,
      filter: () => {
        if (
          includes(
            [
              DISH_FIELDS_CONFIG.NAME.value,
              DISH_FIELDS_CONFIG.DESCRIPTION.value,
              DISH_FIELDS_CONFIG.INGREDIENTS.value,
            ],
            fieldName,
          )
        ) {
          richTextHighlightingInputRef.current.blur()
          toggleEditMode(false)

          return true
        }
        return false
      },
      enableOnTags: HOTKEYS_ENABLED_TAGS,
    },
    [isEditing, toggleEditMode, richTextHighlightingInputRef],
  )

  // Use C hotkey to clear text when row is in focus
  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.C.hotkey,
    handleClearMiscAndChoiceText,
    {
      enabled: !isEditing && isFocusOnCell && !isDisabled && hasMiscAndChoice,
    },
    [
      isEditing,
      isFocusOnCell,
      isDisabled,
      hasMiscAndChoice,
      handleClearMiscAndChoiceText,
    ],
  )

  return (
    <StyledEditableTextDivContainer
      hasMarginTop={hasMarginTop}
      onMouseEnter={isEmptyCurrentValue ? () => toggleEditMode(true) : null}
      onMouseLeave={
        isEmptyCurrentValue && !isFocused ? () => toggleEditMode(false) : null
      }
    >
      {showActionButtons && (
        <StyledEditableTextActionButtonsContainer>
          {hasMiscAndChoice && !isDisabled && (
            <Tooltip title="Clear misc&choice text">
              <Button
                shape="circle"
                onClick={handleClearMiscAndChoiceText}
                icon={<ClearOutlined />}
              />
            </Tooltip>
          )}
          {fieldName === DISH_FIELDS_CONFIG.NAME.value && (
            <Tooltip title="Copy dish name hyperlink">
              <Button
                shape="circle"
                onClick={handleCopyDishNameAndLink}
                icon={<CopyOutlined />}
              />
            </Tooltip>
          )}
        </StyledEditableTextActionButtonsContainer>
      )}

      {isEditing ? (
        <RichTextHighlightingInput
          ref={richTextHighlightingInputRef}
          value={currentValue}
          highlightStrategy={handleAiHighlightStrategy}
          decoratorComponent={AiHighlightDecoratedComponent}
          onChange={handleSave}
          onBlur={() => toggleEditMode(false)}
          highlights={highlights}
          shouldFocusOnMount={!isEmptyCurrentValue}
          onlySelection={!isTextEditingEnabled}
          toolbarItems={['italic', 'underline']}
          isFocused={isFocused}
          setIsFocused={setIsFocused}
        />
      ) : (
        <StyledEditableTextDivCell
          aria-hidden="true"
          isDisabled={isDisabled}
          onClick={() => toggleEditMode(true)}
        >
          <ReadMore
            maxHeight={170}
            component={
              <RichTextHighlightingInput
                value={currentValue}
                highlightStrategy={handleAiHighlightStrategy}
                decoratorComponent={AiHighlightDecoratedComponent}
                highlights={highlights}
                isReadOnly
                isFocused={isFocused}
                setIsFocused={setIsFocused}
              />
            }
          />
        </StyledEditableTextDivCell>
      )}
    </StyledEditableTextDivContainer>
  )
}

DishEditableHighlightingTextFieldCell.propTypes = {
  fieldName: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  isTextEditingEnabled: PropTypes.bool,
  shouldIncreaseMarginOnEdit: PropTypes.bool,
  dish: dishPropType.isRequired,
}

DishEditableHighlightingTextFieldCell.defaultProps = {
  isDisabled: false,
  isTextEditingEnabled: false,
  shouldIncreaseMarginOnEdit: false,
}

export default DishEditableHighlightingTextFieldCell
